// Prequalification funnel — public landing / role fork. Anonymous, no
// portal session; self-wraps in PortalShell with the public-funnel chrome
// (§4 design system). Stays at the reading width — the fork is a simple
// two-card choice, not a two-column surface.

import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/design-system/components/ui";
import { PortalShell } from "../../components/PortalShell";
import { useFunnelCopy, usePartnerBrand, usePrequalBase } from "./usePartnerBrand";
import { PartnerFallbackNotice, PartnerThemeProvider } from "./PartnerThemeProvider";
import { PROGRAM_TERMS_LINE } from "./prequalContent";
import { usePrequalAttributionCapture } from "./prequalAttribution";
import { hasText } from "./blankCopy";

/**
 * Secondary entry to the Deal Explorer.
 *
 * The Explorer has always been anonymous and reachable at `{base}/explore`,
 * but the ONLY door to it was a card on the post-finalize documents view — so
 * a broker who wanted to model a price for a client had to complete a whole
 * prequalification first, or already know the URL. Nothing about access
 * changes here; the route was public before this link existed.
 *
 * A LINK rather than a third card, deliberately: the fork above is this page's
 * job, and a third card of equal weight would compete with the decision the
 * page exists to get.
 */
const EXPLORER_INVITE = "Just want to run the numbers?";
const EXPLORER_LINK_LABEL = "Model a deal at program rates";

export function PrequalifyLandingPage() {
  // The ad may point here or straight at a role funnel; capture is
  // first-write-wins, so every entry page can safely ask.
  usePrequalAttributionCapture("prequalify-landing");
  const navigate = useNavigate();
  const { brand, loading: brandPending, notFound } = usePartnerBrand();
  // Server-published copy — fine print AND marketing — falling back to the
  // compiled-in constants.
  const { administratorLine, indicativeLabel, marketing } = useFunnelCopy();
  const base = usePrequalBase();
  // 🔴 THREE tiers, and the middle one is easy to miss. `funnel-copy` is
  // Phoenix-tier-only, so no PARTNER OVERRIDE ROW may exist in the copy store for
  // these two blocks — but that does NOT mean a partner's headline cannot differ.
  // `PartnerBrand.HeroHeadline` / `HeroSubcopy` are columns on the brand record,
  // edited on the partner-brand admin form and carried on the public skin, and they
  // sit UPSTREAM of the store:
  //
  //     PartnerBrandSkin.heroHeadline → funnel-copy published block → code default
  //        (partner tier)                (Phoenix tier)                (fallback)
  //
  // Reading `IsPhoenixTierOnly` alone leads to the wrong conclusion, which is why
  // this comment also exists at the block registration and why
  // PrequalFunnelHeroChainTests pins the precedence in both directions.
  const heroHeadline = brand?.heroHeadline || marketing.heroHeadline;
  const heroSubcopy = brand?.heroSubcopy || marketing.heroSubcopy;
  return (
    <PartnerThemeProvider brand={brand}>
    <PortalShell pageTitle="Prequalify" publicFunnel partner={brand} brandPending={brandPending}>
      {notFound && <PartnerFallbackNotice />}
      {/* Every published block below drops out when it is blank (D4); the
          accent rule belongs to the headline and goes with it. The role cards
          are buttons, so they stay even with both of their lines blank. */}
      {hasText(marketing.programOverline) && (
        <p
          className="text-label uppercase tracking-widest sm:tracking-[0.24em] text-gold-deep mt-0 mb-space-2"
          data-testid="prequal-program-overline"
        >
          {marketing.programOverline}
        </p>
      )}
      {hasText(heroHeadline) && (
        <>
          <h1 className="text-h3 sm:text-h2 font-display uppercase tracking-wide text-ink m-0">
            {heroHeadline}
          </h1>
          <div
            className="h-[3px] w-16 bg-gold mt-space-2"
            aria-hidden="true"
            data-testid="prequal-hero-rule"
          />
        </>
      )}
      {hasText(heroSubcopy) && (
        <p className="text-small text-ink-mid mt-space-3 mb-0" data-testid="prequal-hero-subcopy">
          {heroSubcopy}
        </p>
      )}

      {/* §7 — sans voice (Montserrat 600, sentence case); serif is reserved
          for rendered dollar figures. */}
      {hasText(marketing.forkQuestion) && (
        <h2 className="font-semibold normal-case tracking-normal text-[1.45rem] text-ink mt-space-6 mb-space-3">
          {marketing.forkQuestion}
        </h2>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-4">
        <button
          type="button"
          onClick={() => navigate(`${base}/buyer`)}
          data-testid="prequal-choose-buyer"
          className="text-left bg-transparent border-0 p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-[14px]"
        >
          <Card className="rounded-[14px] p-space-5 h-full border border-line shadow-[0_10px_34px_rgba(20,20,20,0.07)] hover:border-gold hover:bg-gold-tint transition-colors duration-fast ease-standard">
            {hasText(marketing.forkTitleBuyer) && (
              <p
                className="font-semibold text-[1.1rem] text-ink mt-0 mb-space-2"
                data-testid="prequal-fork-title-buyer"
              >
                {marketing.forkTitleBuyer}
              </p>
            )}
            {hasText(marketing.forkBodyBuyer) && (
              <p className="text-small text-ink-mid m-0" data-testid="prequal-fork-body-buyer">
                {marketing.forkBodyBuyer}
              </p>
            )}
          </Card>
        </button>
        <button
          type="button"
          onClick={() => navigate(`${base}/seller`)}
          data-testid="prequal-choose-seller"
          className="text-left bg-transparent border-0 p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded-[14px]"
        >
          <Card className="rounded-[14px] p-space-5 h-full border border-line shadow-[0_10px_34px_rgba(20,20,20,0.07)] hover:border-gold hover:bg-gold-tint transition-colors duration-fast ease-standard">
            {hasText(marketing.forkTitleSeller) && (
              <p
                className="font-semibold text-[1.1rem] text-ink mt-0 mb-space-2"
                data-testid="prequal-fork-title-seller"
              >
                {marketing.forkTitleSeller}
              </p>
            )}
            {hasText(marketing.forkBodySeller) && (
              <p className="text-small text-ink-mid m-0" data-testid="prequal-fork-body-seller">
                {marketing.forkBodySeller}
              </p>
            )}
          </Card>
        </button>
      </div>

      {/* Built from `base`, so the partner funnel keeps its own namespace. */}
      <p className="text-small text-ink-mid mt-space-5 mb-0">
        {EXPLORER_INVITE}{" "}
        <Link
          to={`${base}/explore`}
          data-testid="prequal-landing-explore"
          className="text-gold-deep underline underline-offset-2 hover:text-gold"
        >
          {EXPLORER_LINK_LABEL}
        </Link>{" "}
        — no application needed.
      </p>

      <div className="mt-space-6 flex flex-col gap-space-2">
        <p className="text-caption text-ink-faint m-0">{PROGRAM_TERMS_LINE}</p>
        {hasText(administratorLine) && (
          <p className="text-caption text-ink-faint m-0" data-testid="prequal-administrator-line">{administratorLine}</p>
        )}
        {hasText(indicativeLabel) && (
          <p className="text-caption text-ink-faint m-0" data-testid="prequal-indicative-label">{indicativeLabel}</p>
        )}
      </div>
    </PortalShell>
    </PartnerThemeProvider>
  );
}
